import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Stack, Group, Title, Text, Paper, Badge, Table, Loader, ScrollArea, Grid, Button,
  SimpleGrid, Anchor, Tooltip, ActionIcon, Alert, UnstyledButton, SegmentedControl, Divider, Tabs,
} from '@mantine/core';
import { IconChevronLeft, IconChevronRight, IconSchool, IconRefresh, IconAlertTriangle, IconSitemap, IconCircleCheck, IconCircleX } from '@tabler/icons-react';
import { useSearchParams } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import useAuthenticatedData from '../../../hooks/useAuthenticatedData';
import useAuthenticatedPost from '../../../hooks/useAuthenticatedPost';
import { useWorkspaceScope } from '../../../workspace/WorkspaceScopeContext';
import config from '../../../config';
import { SalesHistoryPanel, QuoteHistoryPanel, InventoryPanel, PricingPanel, RepairPanel, RfqPanel, OpenDemandPanel, fmtMoney, ClassBadge, DescCell } from './CockpitPanels';
import TeachModal from '../../teaching/TeachModal';
import DecisionCanvas from '../../explain/DecisionCanvas';
import DealCanvas from '../../explain/DealCanvas';
import { Modal as MantineModal, useComputedColorScheme } from '@mantine/core';
import NeuralLoader from '../../common/NeuralLoader';
import DraftBadge from '../../common/DraftBadge';
import cockpitCss from './QuotingCockpit.module.css';

// Coverage is per LINE and binary: we hold the requested qty in the requested condition, or we don't.
// (`have_some` was removed from the engine — a partially-stocked line cannot be filled, so it is have_none.)
const COVERAGE ={ have_all: 'green', have_none: 'red', unknown: 'gray' };
// `hold_for_credit` is emitted by a RULE, not a model (EnrichmentRunner.DeterministicVerdict): the customer
// is on credit hold, so there is nothing to judge and no LLM is called. Red, because it blocks the sale.
const ACTION = { quote_now: 'green', quote_with_review: 'teal', hold_for_sourcing: 'yellow', hold_for_credit: 'red', request_more_info: 'blue', deprioritize: 'gray' };
// The actions that mean "we should quote this line" — the same gate that promotes a line to Nymeria.
const QUOTABLE = new Set(['quote_now', 'quote_with_review']);

// WHY the margin on a line is what it is. ITS buys aircraft on teardown, so parts enter at $0 book cost (98%
// of on-hand units). The airframe's price sits as a base cost on the virtual warehouse (the lot), and each
// sale claws 70-80% of the sale price back to repay it — until the lot is paid off and moves from BCR (Base
// Cost Remaining) to NOBCR. So the SAME $0 unit is ~100% margin out of a paid-off lot and ~25% out of one
// still repaying. Without this badge the margin number is unexplainable. See docs/pentagon_warehouse_cost.md.
const COST_BASIS = {
  NOBCR: { color: 'green', label: 'no BCR', tip: 'The airframe this unit came off is PAID OFF — nothing is clawed back, so the margin is real.' },
  BCR:   { color: 'orange', label: 'BCR', tip: 'Base Cost Remaining — the airframe is still being repaid, so most of the sale goes back to its base cost. Lower margin, but the sale retires the loan and stops the interest.' },
  CONSI: { color: 'gray', label: 'consignment', tip: 'Vendor-owned stock. We can sell it, but the margin is the vendor’s split — it is not our teardown margin.' },
  SPEC:  { color: 'blue', label: 'special', tip: 'Special-handling warehouse.' },
};
// `ship` is the unit we'd actually ship — the OLDEST one (FIFO). It is what the whole tooltip is about: on
// 22% of stocked lines the matching units sit in more than one lot, so WHICH unit ships decides the margin.
function CostBasisBadge({ basis, paybackPct, ship }) {
  const b = basis ? COST_BASIS[String(basis).trim().toUpperCase()] : null;
  if (!b) return <Text size="xs" c="dimmed">—</Text>;
  const pct = paybackPct != null ? ` · ${Number(paybackPct).toFixed(0)}% payback` : '';

  const lines = [b.tip];
  if (paybackPct != null) lines.push(`This lot repays ${Number(paybackPct).toFixed(0)}% of each sale.`);
  if (ship?.dos != null || ship?.received) {
    const age = ship.dos != null ? `${Number(ship.dos).toLocaleString()} days on shelf` : null;
    const from = ship.received ? `received ${fmtDate(ship.received)}` : null;
    const of = ship.units > 1 ? ` (oldest of ${ship.units} matching units)` : '';
    lines.push(`We would ship the OLDEST unit${of}${ship.logno ? `, logno ${ship.logno}` : ''} — ${[from, age].filter(Boolean).join(', ')}. Its lot is what sets the margin.`);
  }

  return (
    <Tooltip label={lines.join(' ')} multiline w={300} withArrow>
      <Badge color={b.color} variant="light" size="sm" style={{ cursor: 'help' }}>{b.label}{pct}</Badge>
    </Tooltip>
  );
}

// PLATFORM ALIGNMENT — how well something lines up with ITS's target platforms (737NG/737CL/777/A320).
// Replaces "customer tier" (key/high/mid/low), which was built from an annual revenue TARGET, not revenue.
//
// `none` is NOT a negative. For a customer it means "no fleet on record" — the normal case for a broker or
// distributor, who doesn't operate aircraft at all (83% of the accounts that send us RFQs). `weak` is the
// real negative: we DO know their fleet, and none of it is ours. Never render the two the same way.
const PLATFORM_FIT = {
  strong: { color: 'green',  label: 'strong' },
  medium: { color: 'teal',   label: 'medium' },
  weak:   { color: 'orange', label: 'weak' },
  none:   { color: 'gray',   label: 'unknown' },
};
function PlatformFitBadge({ level, matches, kind }) {
  const key = (level || 'none').toLowerCase();
  const f = PLATFORM_FIT[key] || PLATFORM_FIT.none;
  const hit = (matches || []).join(', ');
  const isCustomer = kind === 'customer';

  const tip = key === 'none'
    ? (isCustomer
      ? 'No fleet on record for this account — we simply do not know what they operate. Normal for a broker or distributor, who does not fly aircraft. This is NOT "misaligned".'
      : 'No platform data on file for this part — we do not know what it fits.')
    : key === 'weak'
      ? (isCustomer
        ? 'We know their fleet, and none of it is one of our target platforms.'
        : 'We know this part\'s platforms, and none of them is one of our targets.')
      : isCustomer
        ? `Flies ${hit} — ${(matches || []).length > 1 ? 'both target platforms' : 'a target platform'} for ITS.`
        : `This part fits ${hit}${key === 'medium' ? ' (eligible for it, though that is not its main platform)' : ' — its main platform'}.`;

  return (
    <Tooltip label={tip} multiline w={280} withArrow>
      <Badge color={f.color} variant="light" size="sm" style={{ cursor: 'help' }}>
        {f.label}{hit ? ` · ${hit}` : ''}
      </Badge>
    </Tooltip>
  );
}

// Do we have a governed sell price (SSP) for what this line is asking for? Three states, because "stale
// price" and "no price at all" are different problems: a stale SSP still anchors (with a warning), while a
// missing one drops the line to the comp/cost rungs of the pricing ladder.
function PriceCell({ p }) {
  if (!p) return <Text size="xs" c="dimmed">—</Text>;
  const ssp = Number(p.ssp);
  const cond = p.condition_code || 'this condition';

  if (!(ssp > 0)) {
    return (
      <Tooltip label={`No SSP on file for ${cond} — pricing falls back to comp sale or cost + margin.`} multiline w={240} withArrow>
        <IconCircleX size={16} color="var(--mantine-color-red-6)" style={{ cursor: 'help', display: 'block' }} />
      </Tooltip>
    );
  }
  if (p.ssp_expired) {
    return (
      <Tooltip label={`SSP ${fmtMoney(ssp)} for ${cond} is EXPIRED — stale price, verify before quoting.`} multiline w={240} withArrow>
        <IconAlertTriangle size={16} color="var(--mantine-color-yellow-6)" style={{ cursor: 'help', display: 'block' }} />
      </Tooltip>
    );
  }
  return (
    <Tooltip label={`SSP ${fmtMoney(ssp)} for ${cond} — current, not expired.`} multiline w={240} withArrow>
      <IconCircleCheck size={16} color="var(--mantine-color-green-6)" style={{ cursor: 'help', display: 'block' }} />
    </Tooltip>
  );
}
const STANCE = { prioritize: 'green', standard: 'blue', cautious: 'orange', decline: 'red' };
const PAGE = 500;   // stack window; prev/next covers the first PAGE of the filtered set
const parse = (s, d) => { try { return s ? JSON.parse(s) : d; } catch { return d; } };
// Display name for an opportunity — email-sourced leads often have no mapped company/ERP key,
// so fall back to the buyer contact email before giving up with "unknown".
const coName = (r) => r?.buyer_company_name || r?.customer_name_dw || r?.buyer_contact_email || '(unknown)';
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' }) : '—');
// DB datetimes are Arizona local — never TZ-convert. Drop any trailing Z/offset, show date + H:M:S (24h).
const fmtReceivedAZ = (iso) => {
  if (!iso) return '—';
  const d = new Date(String(iso).replace(/(Z|[+-]\d{2}:?\d{2})$/, ''));
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' }) + ' ' +
    d.toLocaleTimeString('en-US', { hour12: true, hour: 'numeric', minute: '2-digit', second: '2-digit' });
};
const timeAgo = (iso) => {
  if (!iso) return '';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 45) return 'just now';
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

export default function QuotingCockpit() {
  const [params, setParams] = useSearchParams();
  const startId = params.get('start');

  // The cockpit is a master/detail view over the SAME filtered+sorted set as the
  // /sales/opportunities list — the shared scope.worklist. It reads those filters (it does
  // not own its own); the list (and Nymeria) drive them. The current deal is the URL
  // ?start= param (source of truth); lineIdx is a local cursor.
  const { scope, setWorklist } = useWorkspaceScope();
  const wl = scope.worklist;
  const [lineIdx, setLineIdx] = useState(0);

  const stackUrl = useMemo(() => {
    const p = new URLSearchParams();
    p.set('page', '0');
    p.set('pageSize', String(PAGE));
    p.set('sortField', wl.sortField || 'rfq_create_date');
    p.set('sortDir', wl.sortDir || 'desc');
    if (wl.enrichmentStatus && wl.enrichmentStatus !== 'all') p.set('enrichmentStatus', wl.enrichmentStatus);
    if (wl.keyOnly) p.set('isKeyCustomer', 'true');
    if (wl.adjOnly) p.set('adjudicationNeeded', 'true');
    if (wl.marketSegment) p.set('marketSegment', wl.marketSegment);
    if (wl.customer) p.set('customer', wl.customer);
    if (wl.salesperson) p.set('salesperson', wl.salesperson);
    if (wl.minScore) p.set('minScore', String(wl.minScore));
    if (wl.from) p.set('from', wl.from);
    if (wl.to) p.set('to', wl.to);
    return `${config.apiBaseUrl}/api/vesper/worklist?${p.toString()}`;
  }, [wl.sortField, wl.sortDir, wl.enrichmentStatus, wl.keyOnly, wl.adjOnly, wl.marketSegment, wl.customer, wl.salesperson, wl.minScore, wl.from, wl.to]);
  const { data: stackData, isLoading: stackLoading } = useAuthenticatedData(stackUrl);
  const stack = useMemo(() => stackData?.rows || [], [stackData]);

  // If the requested deal (?start=) isn't in the current filtered stack, surface it at the
  // front anyway so a deep-link/redirect always lands on the right deal (detail is fetched by id).
  const effStack = useMemo(() => {
    if (startId && !stack.some((r) => r.deal_public_id === startId)) return [{ deal_public_id: startId }, ...stack];
    return stack;
  }, [stack, startId]);

  // Cursor DERIVED from the URL ?start=, so it stays correct as the placeholder appears/disappears.
  const curIndex = startId ? Math.max(0, effStack.findIndex((r) => r.deal_public_id === startId)) : 0;
  const cur = effStack[curIndex];
  const goToDeal = (dealPublicId) => {
    if (!dealPublicId) return;
    const p = new URLSearchParams(params); p.set('start', dealPublicId); setParams(p, { replace: true });
  };
  // Reflect the resolved deal into the URL when it isn't there yet (first load).
  useEffect(() => {
    if (cur?.deal_public_id && cur.deal_public_id !== startId) goToDeal(cur.deal_public_id);
  }, [cur?.deal_public_id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setLineIdx(0); }, [startId]);   // reset line selection when the deal changes

  const dealUrl = cur ? `${config.apiBaseUrl}/api/vesper/deals/${encodeURIComponent(cur.deal_public_id)}` : null;
  const { data: detail, refetch: refetchDeal } = useAuthenticatedData(dealUrl);
  // useAuthenticatedData keeps the PREVIOUS url's data while the new fetch is in flight, so on a
  // deal switch the old opportunity would linger for seconds. Trust `detail` only when it's for
  // the deal currently on screen; otherwise render a loader, not stale data.
  const detailForCur = detail && detail.deal?.deal_public_id === cur?.deal_public_id ? detail : null;

  const prev = () => goToDeal(effStack[Math.max(0, curIndex - 1)]?.deal_public_id);
  const next = () => goToDeal(effStack[Math.min(effStack.length - 1, curIndex + 1)]?.deal_public_id);

  // Keyboard ←/→ (ignore when typing).
  useEffect(() => {
    const onKey = (e) => {
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target?.tagName)) return;
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [curIndex, effStack.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the highlighted master-list row in view as you navigate.
  const activeRowRef = useRef(null);
  useEffect(() => { activeRowRef.current?.scrollIntoView({ block: 'nearest' }); }, [curIndex]);

  const enr = detailForCur?.enrichment || {};
  const deal = detailForCur?.deal || cur || {};   // fall back to the stack row (correct new-deal basics) while loading
  const lines = detailForCur?.lines || [];
  const draft = parse(enr.draft_quote_json, []);
  const scores = parse(enr.numeric_scores_json, null);
  // The deal-level ROLLUPS are deliberately NOT read here any more: liveCoverage (coverage/% of lines/SUM of
  // margins), flags_json and guidance_applied_json.cited all describe the deal or its driving line, and every
  // per-line surface below used to silently fall back to them. Nothing below the "Selected line" divider may
  // read a deal value. adjNeeded stays: it drives the deal-level banner ABOVE the divider.
  const dealSummary = parse(enr.deal_summary_json, null);
  const adjNeeded = enr.adjudication_needed === true || enr.adjudication_needed === 1;
  const acctno = deal.buyer_company_erp_key;
  const selectedLine = lines[lineIdx] || lines[0];
  const partNumber = selectedLine?.part_number;
  const partClass = selectedLine?.part_class || null;
  const dealLineId = selectedLine?.deal_line_id;

  // Vesper judges EACH LINE on its own (grain = the deal line; part + condition ride along), so the
  // judgement below swaps when you pick a different line. Falls back to the deal-level rollup for
  // enrichments made before per-line judgement.
  const lineEnrichments = parse(enr.line_enrichment_json, []) || [];
  const lineEnr = lineEnrichments.find((l) => l.deal_line_id === dealLineId) || null;

  // Per-line priority: the judge's score (base + its nudge) if we have it, else the deterministic base
  // from Step 2's line_scores. Same number the drawer and the judgement panel show for that line.
  const lineScoresById = useMemo(
    () => parse(enr.numeric_scores_json, {})?.line_scores || [],
    [enr.numeric_scores_json],
  );
  const lineScoreOf = (l) =>
    lineEnrichments.find((e) => e.deal_line_id === l.deal_line_id)?.priority_score
    ?? lineScoresById.find((s) => s.deal_line_id === l.deal_line_id)?.priority_score
    ?? null;

  // The SELECTED line's own signals. Vesper scores and judges each line separately, so coverage, margin,
  // rationale, guidance and flags are all line-scoped — the deal-level rollup (enr.*) is only a fallback
  // for enrichments made before per-line judgement existed.
  const lineScore = lineScoresById.find((s) => s.deal_line_id === dealLineId) || null;
  // The selected line's draft row — carries WHICH unit we'd ship (the oldest; FIFO) and how long it has sat.
  // Keyed on THIS line, never on part number: a deal can carry the same part on two lines in different
  // conditions, and a part-number match returns whichever comes first — the sibling's draft row half the time.
  const draftLineNo = lineScoresById.find((x) => x.deal_line_id === dealLineId)?.line_number ?? null;
  const selectedDraft = draftLineNo != null
    ? (Array.isArray(draft) ? draft : []).find((d) => d.line_number === draftLineNo) || null
    : null;
  // THIS line's own judgement output. NO deal fallback, by design: enr.priority_rationale is the DRIVING
  // (max-priority) line's text, guidance_applied_json.cited is that same line's citations, and flags_json is
  // the deal-level summarize rollup ("Multi-line RFQ (4 lines)", "Line 3-4 not fillable"). Falling back put
  // another line's words under the heading "Why this line scores what it does". Missing ⇒ nothing.
  const lineRationale = lineEnr?.priority_rationale ?? null;
  const lineCited = lineEnr?.guidance_applied ?? [];
  const lineFlags = lineEnr?.flags ?? [];

  // Single-line deals get no LLM deal summary (their one line's judgement already IS the deal's verdict, so
  // paying for a second model call to restate it would be waste). Synthesize the same terse headline from
  // that verdict instead — this is presentation of a decision already made, not a new judgement.
  const singleLineSummary = (() => {
    if (dealSummary?.headline || lines.length !== 1) return null;
    const l = lines[0];
    const le = lineEnrichments.find((e) => e.deal_line_id === l.deal_line_id);
    const action = le?.recommended_action ?? null;   // this line's own verdict, or none
    if (!action) return null;

    const d = (Array.isArray(draft) ? draft : []).find((x) => x.part_number === l.part_number) || {};
    const cond = d.condition_code || l.condition_code || '';
    const part = `${l.part_number}${cond ? ` ${cond}` : ''}`;

    if (QUOTABLE.has(action) && d.proposed_unit_price != null)
      return { headline: `Recommend quoting this line. Quote ${part} at ${fmtMoney(d.proposed_unit_price)}.` };
    if (QUOTABLE.has(action))
      return { headline: `Recommend quoting this line — ${part}. No price could be drafted.` };
    return { headline: `Pass on ${part} — ${action.replace(/_/g, ' ')}.` };
  })();
  const topSummary = dealSummary?.headline ? dealSummary : singleLineSummary;

  // Per-line SSP status for the lines table (governed sell price for the requested condition, and whether
  // it has gone stale — A-class expires at 90d, the rest at 180d). Keyed the same way as the draft.
  const pricingLines = parse(enr.pricing_json, {})?.lines || [];
  // Same: the SSP column must describe THIS line's condition. A part-number fallback showed one condition's
  // SSP status on the other condition's row.
  const pricingOf = (l) => {
    const ln = lineScoresById.find((x) => x.deal_line_id === l?.deal_line_id)?.line_number;
    return ln != null ? pricingLines.find((p) => p.line_number === ln) ?? null : null;
  };

  // The draft quote and the requested lines are the same lines in the same order (draft `line_number` is a
  // 1-based ordinal over the deal lines, and both are ordered by deal_line_id) — so selecting in either
  // table selects the same line. Fall back to the part number if a draft row ever lacks its ordinal.
  const lineIdxOfDraft = (d, fallbackIdx) => {
    // Map a draft row back to its requested line by line_number → deal_line_id. No part-number fallback:
    // on a same-part deal it selected whichever line came first, so the two conditions were not separately
    // selectable from the draft table.
    const dlid = lineScoresById.find((x) => x.line_number === d?.line_number)?.deal_line_id;
    const idx = dlid != null ? lines.findIndex((l) => l.deal_line_id === dlid) : -1;
    return idx >= 0 ? idx : fallbackIdx;
  };
  // Same rule for the header badges: the deal columns are a ROLLUP (priority = MAX line; action/stance = the
  // driving line's; adjudication/escalated = OR across lines) and `cur` is that same rollup off the worklist
  // row. A line with no judgement of its own shows none — not a sibling's.
  const lineAction = lineEnr?.recommended_action ?? null;
  const lineStance = lineEnr?.treatment_stance ?? null;
  const linePriority = lineEnr?.priority_score ?? null;
  const lineAdjNeeded = lineEnr?.adjudication_needed === true;
  const lineEscalated = lineEnr?.escalated === true;
  const partClassByPart = useMemo(() => {
    const m = {}; (detailForCur?.lines || []).forEach((l) => { if (l.part_number) m[l.part_number] = l.part_class; }); return m;
  }, [detailForCur]);
  // On-hand + fill for the requested-lines table, read from THIS LINE'S SCORE — the same row the Signals
  // panel and Nymeria read. It used to come from the deal endpoint's `liveInventory` (vesper.vw_deal_line_
  // inventory -> dw.InventoryFact), queried LIVE, while the score sitting in the next column was computed at
  // enrichment time from Pentagon's STOCKWH. The two sources agree on content — verified across 12,425 parts
  // on live deals — but one is live and one is a snapshot, so the instant a unit ships they diverge: the
  // "On hand" column would say 0 while the coverage, margin and draft quote beside it still said have_all.
  // Same fact, two sources, one of them wrong. The enrichment is the world; re-enrich to refresh it.
  const invByPart = useMemo(() => {
    const m = {};
    lineScoresById.forEach((s) => {
      if (s.deal_line_id != null) m[s.deal_line_id] = { current_qty: s.on_hand_qty, has_match: s.fillable };
    });
    return m;
  }, [lineScoresById]);

  // Deterministic STOP/ALERT conditions surfaced under the header (hard-coded, no rules engine —
  // add more entries here as the founder identifies them; nothing here is model-driven).
  // Both alerts now read the ENRICHMENT, not live part lookups: the same rows the AI card beside them was
  // built from. The two live fetches that used to feed the expired-pricing alert (part-facts + inventory)
  // are gone with it — they existed only so the UI could re-derive a verdict the engine had already reached.
  const alerts = useMemo(() => {
    const out = [];
    if (scores?.customer_on_hold) out.push({ key: 'on_hold', label: 'Customer on hold', color: 'red' });
    // Expired SSP — read STRAIGHT off the pricing engine's verdict for this line. It used to be recomputed
    // here in JS from the raw part facts, and the two implementations disagreed on the case that matters
    // most: the engine treats a BLANK priced-date as expired (we have no idea how old this price is), while
    // the JS returned false for a null date and never expired a part whose class was outside A-D. So the
    // lines table could show the ⚠ EXPIRED icon (from pricing_json) while the Pricing panel two feet below
    // rendered the same SSP in plain black, and Nymeria warned about stale pricing the rep saw no sign of.
    // One rule, in C#, where the engine that uses it lives.
    if (pricingOf(selectedLine)?.ssp_expired) {
      out.push({ key: 'expired_pricing', label: 'Expired pricing', color: 'orange' });
    }
    return out;
  }, [scores, selectedLine, pricingLines]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cockpit advisor — headline + guidance; cached server-side, ?refresh=true only when regenerated.
  const [refreshKey, setRefreshKey] = useState(null);
  useEffect(() => { setRefreshKey(null); }, [startId, lineIdx]);
  // Nymeria recommends PER LINE — key the request (and the cache) on the deal line, not the part.
  const curKey = cur ? `${cur.deal_public_id}|${dealLineId || ''}` : null;
  const headlineUrl = useMemo(() => {
    if (!cur || !dealLineId) return null;
    const p = new URLSearchParams();
    p.set('dealLineId', String(dealLineId));
    if (partNumber) p.set('partNumber', partNumber);
    if (refreshKey && refreshKey === curKey) p.set('refresh', 'true');
    return `${config.apiBaseUrl}/api/vesper/deals/${encodeURIComponent(cur.deal_public_id)}/headline?${p.toString()}`;
  }, [cur, dealLineId, partNumber, refreshKey, curKey]);
  const { data: headline, isLoading: headlineLoading, refetch: refetchHeadline } = useAuthenticatedData(headlineUrl);
  // Same stale-guard as the detail: only trust the headline when it's for the current deal.
  const headlineForCur = headline && headline.dealPublicId === cur?.deal_public_id ? headline : null;
  const hr = headlineForCur?.result || {};
  const regenerateHeadline = () => {
    if (refreshKey === curKey) refetchHeadline();
    else setRefreshKey(curKey);
  };

  // Explain — the decision-transparency canvas for this deal + selected part.
  const [explainOpen, setExplainOpen] = useState(false);
  // The trace opens on the grain you clicked from. The deal headline's icon opens "The whole deal" (the
  // fan-out/fan-in, where the customer brief and deal summary live); the per-line panels open "This line".
  // Opening a line trace from a deal-level summary just makes the reader do the translation themselves.
  const [explainTab, setExplainTab] = useState('line');
  const openExplain = (tab) => { setExplainTab(tab); setExplainOpen(true); };
  const colorScheme = useComputedColorScheme('light');
  // Teach — the boss corrects a surface's output.
  const [teach, setTeach] = useState(null);
  // The teach payload. This string is PERSISTED as a training example, so contamination here is permanent:
  // it used to carry the deal's coverage rollup, "% of LINES in stock", the SUM of every line's margin, and
  // the deal-level opportunity_summary naming the other parts — and no condition, so on a same-part deal both
  // lines produced an identical example. It is now strictly THIS line.
  const situation = () => [
    `Customer: ${coName(deal)} · segment ${deal.MarketSegment || '—'}`,
    partNumber
      ? `Line: ${partNumber} ${selectedLine?.condition_code || '(no condition specified)'} × ${selectedLine?.quantity ?? '?'}`
        + `${selectedLine?.description ? ` — ${selectedLine.description}` : ''}`
      : '',
    lineScore
      ? `Coverage (this line): ${lineScore.coverage || '—'}`
        + `${lineScore.margin_dollars != null ? ` · est. gross margin ${fmtMoney(lineScore.margin_dollars)}` : ''}`
        + `${lineScore.cost_basis ? ` · cost basis ${lineScore.cost_basis}` : ''}`
      : '',
    lineRationale ? `Why this line scores what it does: ${lineRationale}` : '',
    lineEnr?.line_summary ? `This line: ${lineEnr.line_summary}` : '',
    // The customer brief is ACCOUNT-grain — it names no part, so it is safe in a persisted training example.
    enr.customer_summary ? `Customer: ${enr.customer_summary}` : '',
  ].filter(Boolean).join('\n');

  const { post } = useAuthenticatedPost();

  // Quote action (stub — records intent-to-quote).
  const [quoting, setQuoting] = useState(false);
  const [quoteMsg, setQuoteMsg] = useState(null);
  const [quoteTick, setQuoteTick] = useState(0);
  const quotesUrl = cur ? `${config.apiBaseUrl}/api/vesper/deals/${encodeURIComponent(cur.deal_public_id)}/quotes?t=${quoteTick}` : null;
  const { data: recordedQuotes } = useAuthenticatedData(quotesUrl);
  const recordQuote = async () => {
    if (!cur || !selectedLine) return;
    setQuoting(true); setQuoteMsg(null);
    try {
      const inv = invByPart[selectedLine.deal_line_id] || {};
      // THIS line's draft row, keyed on the line — never a part-number match. On a deal carrying the same
      // part on two lines in different conditions, the part match returned whichever came first, so we could
      // POST the SIBLING condition's price against this deal_line_id.
      const draftLine = selectedDraft;
      const price = draftLine?.proposed_unit_price ?? inv.sell_price ?? null;
      const res = await post(`${config.apiBaseUrl}/api/vesper/deals/${encodeURIComponent(cur.deal_public_id)}/quote`, {
        dealLineId: selectedLine.deal_line_id,
        partNumber: selectedLine.part_number,
        conditionCode: selectedLine.condition_code,
        quantity: selectedLine.quantity,
        proposedUnitPrice: price,
      });
      setQuoteMsg({ ok: true, text: `Quote recorded (#${res?.id}) — ${selectedLine.part_number}` });
      setQuoteTick((t) => t + 1);
    } catch (e) {
      setQuoteMsg({ ok: false, text: `Failed: ${e?.response?.data?.message || e.message}` });
    } finally { setQuoting(false); }
  };

  // Re-enrich / adjudicate this one deal (ported from the old detail page).
  const [enriching, setEnriching] = useState(false);
  const runEnrich = async (adjudicate) => {
    if (!cur) return;
    setEnriching(true);
    try {
      await post(`${config.apiBaseUrl}/api/vesper/deals/${encodeURIComponent(cur.deal_public_id)}/enrich${adjudicate ? '?adjudicate=true' : ''}`, {});
      notifications.show({ message: 'Enrichment complete.', color: 'green', position: 'bottom-right', autoClose: 3000 });
      refetchDeal?.();
      setRefreshKey(curKey);   // headline auto-invalidates on re-enrich; force a regen
    } catch {
      notifications.show({ message: 'Enrichment failed.', color: 'red', position: 'bottom-right', autoClose: 5000 });
    } finally { setEnriching(false); }
  };


  return (
    <Stack gap="sm" p="md">
      <DraftBadge />
      {stackLoading && <Group justify="center" p="xl"><Loader /></Group>}
      {!stackLoading && effStack.length === 0 && <Text c="dimmed" ta="center" py="xl">No deals match this filter.</Text>}

      {cur && (
        <Group align="flex-start" wrap="nowrap" gap="sm">
          {/* MASTER — a reduced copy of the opportunity list; mirrors the list's filters + sort. */}
          <Paper withBorder radius="md" p="xs" w={232}
            style={{ flexShrink: 0, alignSelf: 'flex-start', position: 'sticky', top: 8 }}>
            {/* Sort selector — writes the shared worklist sort; the selected deal stays put
                (it's anchored by the URL ?start=, so it just moves to its new position). */}
            <SegmentedControl fullWidth size="xs" mb={6}
              value={wl.sortField === 'priority_score' ? 'score' : 'received'}
              onChange={(v) => setWorklist({ sortField: v === 'score' ? 'priority_score' : 'rfq_create_date', sortDir: 'desc' })}
              data={[{ label: 'Score', value: 'score' }, { label: 'Received', value: 'received' }]}
            />
            <Group justify="space-between" mb={6} px={4} wrap="nowrap">
              <Text fw={600} size="xs">{effStack.length ? `${curIndex + 1} / ${effStack.length}` : '—'}</Text>
              <Group gap={2} wrap="nowrap">
                <ActionIcon size="sm" variant="subtle" onClick={prev} disabled={curIndex <= 0}><IconChevronLeft size={15} /></ActionIcon>
                <ActionIcon size="sm" variant="subtle" onClick={next} disabled={curIndex >= effStack.length - 1}><IconChevronRight size={15} /></ActionIcon>
              </Group>
            </Group>
            <ScrollArea.Autosize mah="calc(100vh - 130px)" type="hover">
              <Stack gap={2}>
                {effStack.map((r) => {
                  const active = r.deal_public_id === cur.deal_public_id;
                  return (
                    <UnstyledButton key={r.deal_public_id} ref={active ? activeRowRef : undefined}
                      onClick={() => goToDeal(r.deal_public_id)}
                      style={{ padding: '4px 6px', borderRadius: 6, background: active ? 'var(--mantine-color-blue-light)' : undefined }}>
                      <Group gap={6} wrap="nowrap" align="flex-start">
                        <Badge size="xs" color="indigo" variant={active ? 'filled' : 'light'} style={{ flexShrink: 0 }}>
                          {r.priority_score ?? '—'}
                        </Badge>
                        <div style={{ minWidth: 0 }}>
                          <Text size="xs" fw={active ? 700 : 500} lineClamp={1}>{coName(r)}</Text>
                          <Text size="10px" c="dimmed" lineClamp={1}>{fmtReceivedAZ(r.rfq_create_date)}</Text>
                        </div>
                      </Group>
                    </UnstyledButton>
                  );
                })}
                {effStack.length === 0 && <Text size="xs" c="dimmed" ta="center" py="sm">No deals.</Text>}
              </Stack>
            </ScrollArea.Autosize>
          </Paper>

          {/* DETAIL */}
          <Stack gap="sm" style={{ flex: 1, minWidth: 0 }}>
            {/* Deal header */}
            <Paper withBorder radius="md" p="sm">
              <Group justify="space-between" align="flex-start">
                <div>
                  <Group gap="xs">
                    <Title order={3}>{coName(deal)}</Title>
                    {deal.buyer_company_erp_key && <Text c="dimmed" size="sm">{deal.buyer_company_erp_key}</Text>}
                    {deal.deal_internal_id && (
                      <Anchor size="xs" href={`https://app.rotabull.com/deals/${deal.deal_internal_id}?page=1`} target="_blank" rel="noopener noreferrer">Rotabull ↗</Anchor>
                    )}
                  </Group>
                  <Text size="sm" c="dimmed">
                    {deal.MarketSegment || '—'} · rep {deal.assigned_salesperson_name || cur.assigned_salesperson_name || '—'} ·
                    {' '}created {fmtDate(deal.rfq_create_date)} · due {fmtDate(deal.rfq_response_due_by)}
                  </Text>
                </div>
                <Stack gap={4} align="flex-end" style={{ maxWidth: '68%' }}>
                  <Group gap="xs" justify="flex-end" align="center" wrap="wrap">
                    {/* Per-LINE judgement — these swap when you select a different line. */}
                    <Badge size="lg" color="indigo" variant="filled">Priority: {linePriority ?? '—'}</Badge>
                    {lineAction && <Badge size="lg" color={ACTION[lineAction] || 'gray'} variant="light">Recommendation: {lineAction.replace(/_/g, ' ')}</Badge>}
                    {lineStance && <Badge size="lg" color={STANCE[lineStance] || 'gray'} variant="dot">Suggested stance: {lineStance}</Badge>}
                    {lineEscalated && <Badge size="lg" color="grape" variant="filled">escalated</Badge>}
                    {lineAdjNeeded && <Badge size="lg" color="orange" variant="light">review</Badge>}
                    {Array.isArray(recordedQuotes) && recordedQuotes.length > 0 && (
                      <Tooltip label={`${recordedQuotes.length} quote action(s) recorded for this deal`}>
                        <Badge color="green" variant="light">✓ quoted ×{recordedQuotes.length}</Badge>
                      </Tooltip>
                    )}
                    <Button size="sm" variant="light" leftSection={<IconRefresh size={14} />} loading={enriching} onClick={() => runEnrich(false)}>Re-enrich</Button>
                    {/* Inert for now — the handler stays wired for when quoting is turned on. The Tooltip
                        needs a wrapper: a disabled button emits no pointer events of its own. */}
                    <Tooltip label="Quoting from the cockpit isn't enabled yet.">
                      <div style={{ marginLeft: 'var(--mantine-spacing-md)' }}>
                        <Button size="sm" color="green" disabled loading={quoting} onClick={recordQuote}>
                          Quote {selectedLine?.part_number ? `— ${selectedLine.part_number}` : ''}
                        </Button>
                      </div>
                    </Tooltip>
                  </Group>
                  {quoteMsg && <Text size="xs" c={quoteMsg.ok ? 'green' : 'red'}>{quoteMsg.text}</Text>}
                </Stack>
              </Group>
            </Paper>

            {/* Colours live in QuotingCockpit.module.css, keyed off [data-mantine-color-scheme]. */}
            {adjNeeded && (
              <Alert color="orange" icon={<IconAlertTriangle size={16} />} title="Needs manager review" py="xs"
                className={cockpitCss.adjAlert}>
                {enr.adjudication_reason || 'The judge flagged this opportunity for adjudication.'}
              </Alert>
            )}

            {/* Deal summary — the whole opportunity across its lines (which lines to quote, what we can't
                fill). Multi-line deals get an LLM synthesis over the per-line verdicts; single-line deals
                fall back to the deal-level brief, since their one line's judgement already says it all. */}
            <Paper withBorder radius="md" p="md" style={{ borderLeft: '4px solid var(--mantine-color-blue-6)' }}>
              {enriching ? (
                <NeuralLoader size={34} label="Re-enriching this deal…" />
              ) : !detailForCur ? (
                <NeuralLoader size={34} label="Reading the whole picture…" />
              ) : topSummary?.headline ? (
                <Stack gap={4}>
                  <Group justify="space-between" align="flex-start" wrap="nowrap">
                    <Text fw={700} size="lg">{topSummary.headline}</Text>
                    <Group gap={6} wrap="nowrap" style={{ flexShrink: 0 }}>
                      <Badge size="xs" variant="light" color="blue">{lines.length} line{lines.length === 1 ? '' : 's'}</Badge>
                      <Tooltip label="Explain — what data, guidance & models produced this">
                        <ActionIcon size="sm" variant="subtle" color="blue" onClick={() => openExplain('deal')}>
                          <IconSitemap size={15} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Group>
                  {topSummary.summary && <Text size="sm" c="dimmed">{topSummary.summary}</Text>}
                  {Array.isArray(topSummary.considerations) && topSummary.considerations.length > 0 && (
                    <Group gap={6} mt={2}>{topSummary.considerations.map((c, i) => <Badge key={i} size="xs" variant="light" color="gray">{c}</Badge>)}</Group>
                  )}
                </Stack>
              ) : (enr.customer_summary || lineEnr?.line_summary) ? (
                // THE SELECTED LINE's summary, then the customer brief. This used to show
                // `opportunity_summary` — DEAL-grain prose ("2 lines… line 2's price is expired… total margin
                // 1,400") sitting above a panel showing ONE line, describing parts the rep isn't looking at.
                // It is replaced by a per-line summary that follows the line selector. The customer brief
                // stays: its grain is the ACCOUNT, so it is the same on every line and mentions no part.
                <Stack gap={4}>
                  {lineEnr?.line_summary && <Text fw={600} size="sm">{lineEnr.line_summary}</Text>}
                  {enr.customer_summary && <Text size="sm" c="dimmed">{enr.customer_summary}</Text>}
                </Stack>
              ) : (
                <Text size="sm" c="dimmed">No summary yet — re-enrich the deal.</Text>
              )}
            </Paper>

            {!detailForCur ? (
              <Group justify="center" p="xl"><Loader /></Group>
            ) : (
            <>
            {/* STOP / ALERT conditions — deterministic, hard-coded (no rules engine). */}
            {alerts.length > 0 && (
              <Group gap="xs">
                {alerts.map((a) => (
                  <Badge key={a.key} size="xl" color={a.color} variant="filled" leftSection={<IconAlertTriangle size={15} />}>
                    {a.label}
                  </Badge>
                ))}
              </Group>
            )}

            {/* Requested lines | AI draft quote — side by side. Each line carries its OWN judgement now,
                so the Action column is the point: it shows which lines are worth quoting. */}
            <Grid gutter="md">
              <Grid.Col span={{ base: 12, md: 6 }}>
            <Paper withBorder radius="md" p="sm" h="100%">
              <Text fw={600} size="sm" mb="xs">Requested lines ({lines.length}) — click a part</Text>
              <ScrollArea type="auto">
              <Table fz="xs" highlightOnHover>
                <Table.Thead><Table.Tr>
                  <Table.Th>Part</Table.Th><Table.Th>Description</Table.Th><Table.Th>Class</Table.Th><Table.Th>Cond</Table.Th><Table.Th ta="right">Req</Table.Th>
                  <Table.Th ta="right">On hand</Table.Th><Table.Th>Fill</Table.Th><Table.Th ta="center">Price</Table.Th>
                  <Table.Th ta="right">Score</Table.Th><Table.Th>Action</Table.Th>
                </Table.Tr></Table.Thead>
                <Table.Tbody>
                  {lines.map((l, i) => {
                    const inv = invByPart[l.deal_line_id] || {};
                    const le = lineEnrichments.find((e) => e.deal_line_id === l.deal_line_id);
                    return (
                      <Table.Tr key={l.deal_line_id} style={{ cursor: 'pointer', background: i === lineIdx ? 'var(--mantine-color-blue-light)' : undefined }} onClick={() => setLineIdx(i)}>
                        <Table.Td fw={i === lineIdx ? 700 : 500}>{l.part_number}</Table.Td>
                        <Table.Td><DescCell text={l.description} /></Table.Td>
                        <Table.Td><ClassBadge cls={l.part_class} /></Table.Td>
                        <Table.Td>{l.condition_code || '—'}</Table.Td>
                        <Table.Td ta="right">{l.quantity}</Table.Td>
                        <Table.Td ta="right">{inv.current_qty ?? '—'}</Table.Td>
                        <Table.Td><Badge size="xs" color={inv.has_match ? 'green' : 'red'} variant="light">{inv.has_match ? 'yes' : 'no'}</Badge></Table.Td>
                        <Table.Td ta="center"><Group justify="center" gap={0}><PriceCell p={pricingOf(l, i)} /></Group></Table.Td>
                        <Table.Td ta="right">
                          {lineScoreOf(l) != null
                            ? <Text size="xs" fw={i === lineIdx ? 700 : 500}>{lineScoreOf(l)}</Text>
                            : <Text size="xs" c="dimmed">—</Text>}
                        </Table.Td>
                        <Table.Td>
                          {le?.recommended_action
                            ? <Badge size="xs" color={ACTION[le.recommended_action] || 'gray'} variant="light" style={{ whiteSpace: 'nowrap' }}>{le.recommended_action.replace(/_/g, ' ')}</Badge>
                            : <Text size="xs" c="dimmed">—</Text>}
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                  {lines.length === 0 && <Table.Tr><Table.Td colSpan={10}><Text c="dimmed" ta="center" py="sm">No lines.</Text></Table.Td></Table.Tr>}
                </Table.Tbody>
              </Table>
              </ScrollArea>
            </Paper>
              </Grid.Col>

              <Grid.Col span={{ base: 12, md: 6 }}>
            {Array.isArray(draft) && draft.length > 0 && (
              <Paper withBorder radius="md" p="sm" h="100%">
                <Text fw={600} size="sm" mb="xs">AI draft quote — click a part</Text>
                <ScrollArea type="auto">
                <Table fz="xs" highlightOnHover>
                  <Table.Thead><Table.Tr>
                    <Table.Th>Part</Table.Th><Table.Th>Cond</Table.Th><Table.Th ta="right">Qty</Table.Th>
                    <Table.Th ta="right">Avail</Table.Th><Table.Th ta="right">DOS</Table.Th><Table.Th ta="right">Cost</Table.Th>
                    <Table.Th ta="right">Proposed</Table.Th><Table.Th ta="right">Est. GM</Table.Th><Table.Th>Basis</Table.Th><Table.Th>Fill</Table.Th>
                  </Table.Tr></Table.Thead>
                  <Table.Tbody>
                    {draft.map((d, i) => {
                      const idx = lineIdxOfDraft(d, i);
                      const sel = idx === lineIdx;
                      return (
                      <Table.Tr key={i} style={{ cursor: 'pointer', background: sel ? 'var(--mantine-color-blue-light)' : undefined }} onClick={() => setLineIdx(idx)}>
                        <Table.Td fw={sel ? 700 : undefined}>
                          <Group gap={4} wrap="nowrap">
                            <Text span size="xs" fw={sel ? 700 : undefined}>{d.part_number || '—'}</Text>
                            <ClassBadge cls={partClassByPart[d.part_number]} />
                          </Group>
                        </Table.Td>
                        <Table.Td>{d.condition_code || '—'}</Table.Td>
                        <Table.Td ta="right">{d.quantity ?? '—'}</Table.Td>
                        <Table.Td ta="right">{d.available_qty ?? '—'}</Table.Td>
                        {/* Days on shelf of the unit we'd actually ship — the OLDEST one (FIFO). Aged stock
                            drives the pricing discount, and its lot is what sets the margin. */}
                        <Table.Td ta="right">
                          {d.ship_dos_days != null ? (
                            <Tooltip
                              label={`We'd ship the oldest unit${d.ship_units_available > 1 ? ` of ${d.ship_units_available} matching` : ''}${d.logno ? ` (logno ${d.logno})` : ''} — received ${fmtDate(d.ship_received_date)}, ${Number(d.ship_dos_days).toLocaleString()} days on shelf.`}
                              multiline w={260} withArrow>
                              <Text size="xs" style={{ cursor: 'help' }}
                                c={d.ship_dos_days >= 271 ? 'orange' : undefined}
                                fw={d.ship_dos_days >= 271 ? 600 : undefined}>
                                {Number(d.ship_dos_days).toLocaleString()}d
                              </Text>
                            </Tooltip>
                          ) : <Text size="xs" c="dimmed">—</Text>}
                        </Table.Td>
                        <Table.Td ta="right">{fmtMoney(d.unit_cost)}</Table.Td>
                        <Table.Td ta="right"><Text size="xs" fw={700}>{fmtMoney(d.proposed_unit_price)}</Text></Table.Td>
                        {/* ESTIMATED gross margin — sell less the lot's cost of the sale (airframe payback),
                            not sell less the $0 book cost. The Basis column is why the number is what it is. */}
                        <Table.Td ta="right">
                          {d.est_gross_margin != null ? (
                            <Tooltip label={`Est. gross margin ${fmtMoney(d.est_gross_margin)} — sell ${fmtMoney(d.proposed_unit_price)} less est. cost of sale ${fmtMoney(d.est_cogs)}.`} multiline w={260} withArrow>
                              <Group gap={4} justify="flex-end" wrap="nowrap" style={{ cursor: 'help' }}>
                                <Text size="xs" fw={600}>{fmtMoney(d.est_gross_margin)}</Text>
                                {d.margin_pct != null && (
                                  <Text size="xs" c="dimmed">({Number(d.margin_pct).toFixed(0)}%)</Text>
                                )}
                              </Group>
                            </Tooltip>
                          ) : <Text size="xs" c="dimmed">—</Text>}
                        </Table.Td>
                        <Table.Td><CostBasisBadge basis={d.cost_basis} paybackPct={d.payback_pct}
                          ship={{ dos: d.ship_dos_days, received: d.ship_received_date, units: d.ship_units_available, logno: d.logno }} /></Table.Td>
                        {/* We never price a condition the customer didn't ask for. When we can't fill the
                            requested one, show what we DO hold so a substitution can be made deliberately. */}
                        <Table.Td>
                          {!d.can_fill && d.alt_conditions ? (
                            <Tooltip label={`No ${d.condition_code || 'requested'} stock. We hold: ${d.alt_conditions} — substitute only if the customer accepts it.`} multiline w={260} withArrow>
                              <Badge size="xs" color="orange" variant="light" style={{ cursor: 'help' }}>{d.alt_conditions}</Badge>
                            </Tooltip>
                          ) : (
                            <Badge size="xs" color={d.can_fill ? 'green' : 'red'} variant="light">{d.can_fill ? 'yes' : 'no'}</Badge>
                          )}
                        </Table.Td>
                      </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>
                </ScrollArea>
              </Paper>
            )}
              </Grid.Col>
            </Grid>

            {/* ── Everything BELOW this line is scoped to the ONE selected requested line, not the deal.
                   The banner names it explicitly, because otherwise it is not obvious that the panels
                   below swap when you click a different part above. ── */}
            <Divider
              my={4}
              labelPosition="left"
              label={
                <Group gap={8} wrap="nowrap">
                  <Text size="xs" fw={700} tt="uppercase" c="grape.5" style={{ letterSpacing: 0.5 }}>
                    Selected line
                  </Text>
                  {selectedLine ? (
                    <>
                      <Text size="sm" fw={700}>{partNumber}</Text>
                      <Badge size="xs" variant="light" color="grape">{selectedLine.condition_code || 'no cond'}</Badge>
                      {selectedLine.description && (
                        <Text size="xs" c="dimmed">{selectedLine.description}</Text>
                      )}
                      <Text size="xs" c="dimmed">· everything below is for this line</Text>
                    </>
                  ) : (
                    <Text size="xs" c="dimmed">click a requested line above</Text>
                  )}
                </Group>
              }
            />

            {/* Nymeria — THIS LINE's recommendation. Sits under the tables because it's line-scoped: it
                swaps when you click a different requested line. */}
            <Paper withBorder radius="md" p="md" style={{ borderLeft: '4px solid var(--mantine-color-grape-6)' }}>
              <Group justify="space-between" align="center" mb={hr.headline || hr.warranted === false ? 6 : 0}>
                <Text size="xs" c="dimmed" fw={600} tt="uppercase" style={{ letterSpacing: 0.3 }}>
                  AI Sales Agent analysis and suggestion
                </Text>
              </Group>
              {enriching ? (
                <NeuralLoader size={34} label="Re-enriching this deal…" />
              ) : hr.warranted === false ? (
                <Text size="sm" c="dimmed" fs="italic">{hr.headline}</Text>
              ) : hr.headline ? (
                <Stack gap={4}>
                  <Group justify="space-between" align="flex-start" wrap="nowrap">
                    <Text fw={700} size="lg">{hr.headline}</Text>
                    <Group gap={6} wrap="nowrap" style={{ flexShrink: 0 }}>
                      {headline?.generatedAt && (
                        <Text size="xs" c="dimmed" title={fmtDate(headline.generatedAt)}>generated {timeAgo(headline.generatedAt)}</Text>
                      )}
                      <Tooltip label="Regenerate — re-read the picture and rewrite the headline">
                        <ActionIcon size="sm" variant="subtle" color="gray" loading={headlineLoading} onClick={regenerateHeadline}>
                          <IconRefresh size={15} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="Explain — what data, guidance & models produced this">
                        <ActionIcon size="sm" variant="subtle" color="blue" onClick={() => openExplain('line')}>
                          <IconSitemap size={15} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="Teach — correct how the advisor should put this">
                        <ActionIcon size="sm" variant="light" color="grape"
                          onClick={() => setTeach({ surface: 'headline', context: situation(), idealOutput: [hr.headline, hr.guidance].filter(Boolean).join('\n') })}>
                          <IconSchool size={15} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Group>
                  {hr.guidance && <Text size="sm" c="dimmed">{hr.guidance}</Text>}
                  {Array.isArray(hr.considerations) && hr.considerations.length > 0 && (
                    <Group gap={6} mt={2}>{hr.considerations.map((c, i) => <Badge key={i} size="xs" variant="light" color="gray">{c}</Badge>)}</Group>
                  )}
                  {Array.isArray(hr.guidance_applied) && hr.guidance_applied.length > 0 && (
                    <Group gap={4} mt={2}>{hr.guidance_applied.map((g, i) => <Badge key={i} size="xs" variant="dot" color="grape">{g}</Badge>)}</Group>
                  )}
                </Stack>
              ) : headlineForCur ? (
                <Text size="sm" c="dimmed">{headline?.error ? `Advisor unavailable: ${headline.error}` : 'No recommendation.'}</Text>
              ) : (
                <NeuralLoader size={34} label="Reading this line…" />
              )}
            </Paper>

            <Grid gutter="md">
              {/* Left — enrichment */}
              <Grid.Col span={{ base: 12, md: 5 }}>
                <Stack gap="sm">
                  <Paper withBorder radius="md" p="sm">
                    <Group justify="space-between" mb="xs">
                      <Text fw={600} size="sm">Signals & judgment{partNumber ? ` — ${partNumber}` : ''}</Text>
                      <Group gap={4} wrap="nowrap">
                      <Tooltip label="Explain — what data, guidance & models produced this">
                        <ActionIcon size="sm" variant="subtle" color="blue" onClick={() => openExplain('line')}>
                          <IconSitemap size={15} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="Teach — correct the quoting judgment (action / rationale)">
                        <ActionIcon size="sm" variant="light" color="violet"
                          onClick={() => setTeach({ surface: 'judgement', context: situation(),
                            idealOutput: [
                              `Recommended action: ${lineAction || '—'}`,
                              lineStance ? `Treatment stance: ${lineStance}` : '',
                              lineRationale ? `Rationale: ${lineRationale}` : '',
                            ].filter(Boolean).join('\n') })}>
                          <IconSchool size={15} />
                        </ActionIcon>
                      </Tooltip>
                      </Group>
                    </Group>
                    {/* Line-scoped: coverage, margin and score are THIS line's, not the deal's. The deal's
                        margin is the sum across fillable lines, so showing it here never changed when you
                        clicked a different part. */}
                    <SimpleGrid cols={2} mb="xs">
                      <Stat label="Coverage (this line)" value={
                        <Badge color={COVERAGE[lineScore?.coverage] || 'gray'} variant="light">
                          {(lineScore?.coverage ?? '—').replace(/_/g, ' ')}
                        </Badge>} />
                      <Stat label="Est. gross margin (this line)" value={
                        <Stack gap={0}>
                          <Group gap={6} wrap="nowrap">
                            <Text size="sm" fw={600}>{lineScore?.margin_dollars != null ? fmtMoney(lineScore.margin_dollars) : '—'}</Text>
                            {lineScore?.margin_pct != null && (
                              <Text size="xs" c="dimmed">{Number(lineScore.margin_pct).toFixed(0)}%</Text>
                            )}
                          </Group>
                          {/* WHICH unit these numbers describe. We ship the oldest (FIFO), and its lot is what
                              sets the margin — so on a line with units in several lots, this is load-bearing. */}
                          {selectedDraft?.ship_dos_days != null && (
                            <Text size="xs" c="dimmed">
                              oldest unit{selectedDraft.ship_units_available > 1 ? ` of ${selectedDraft.ship_units_available}` : ''}
                              {' · '}{Number(selectedDraft.ship_dos_days).toLocaleString()}d on shelf
                            </Text>
                          )}
                        </Stack>} />
                      <Stat label="Cost basis" value={<CostBasisBadge basis={lineScore?.cost_basis} paybackPct={lineScore?.payback_pct}
                        ship={selectedDraft ? { dos: selectedDraft.ship_dos_days, received: selectedDraft.ship_received_date, units: selectedDraft.ship_units_available, logno: selectedDraft.logno } : null} />} />
                      <Stat label="Priority (this line)" value={lineScoreOf(selectedLine || {}) ?? '—'} />
                      <Stat label="Platform fit (part)" value={
                        <PlatformFitBadge kind="part"
                          level={lineScore?.platform_alignment}
                          matches={lineScore?.platform_matches} />} />
                      <Stat label="Platform fit (customer)" value={
                        <PlatformFitBadge kind="customer"
                          level={scores?.customer_platform_alignment}
                          matches={scores?.customer_platform_matches} />} />
                      <Stat label="Days open (deal)" value={scores?.days_open ?? '—'} />
                    </SimpleGrid>
                    {lineEnr?.line_summary && <><Text size="xs" c="dimmed">This line</Text><Text size="sm" mb="xs">{lineEnr.line_summary}</Text></>}
                    {/* "Customer", not "Customer (deal-level)" — the brief's grain is the ACCOUNT, so it is
                        the same on every line and every deal they send us, not a property of this deal. */}
                    {enr.customer_summary && <><Text size="xs" c="dimmed">Customer</Text><Text size="sm" mb="xs">{enr.customer_summary}</Text></>}
                    {lineRationale && <><Text size="xs" c="dimmed">Why this line scores what it does</Text><Text size="sm">{lineRationale}</Text></>}
                    {lineCited.length > 0 && <Group gap={4} mt="xs">{lineCited.map((g, i) => <Badge key={i} size="xs" color="teal" variant="light">{g}</Badge>)}</Group>}
                    {lineFlags.length > 0 && <Group gap={4} mt="xs">{lineFlags.map((f, i) => <Badge key={i} size="xs" color="yellow" variant="light">{typeof f === 'string' ? f : (f.message || f.type)}</Badge>)}</Group>}
                  </Paper>

                  {/* Open demand — who ELSE is chasing this part. Nymeria has always reasoned over this and
                      been told to let it move the call; until now the rep had no way to see it, so the AI
                      could cite a competing quote the rep could not find anywhere on the screen. */}
                  {deal.deal_public_id && dealLineId && (
                    <OpenDemandPanel dealPublicId={deal.deal_public_id} dealLineId={dealLineId} />
                  )}

                  {/* Repair history for the selected part (moved here from the part-history column) */}
                  {partNumber && <RepairPanel partNumber={partNumber} />}

                  {/* Pricing probes — what competitors sell it for, and what a repair costs */}
                  {partNumber && <RfqPanel partNumber={partNumber} />}
                </Stack>
              </Grid.Col>

              {/* Right — decision history for the selected part */}
              <Grid.Col span={{ base: 12, md: 7 }}>
                <Stack gap="sm">
                  <Group gap="xs">
                    <Text fw={700} size="sm">Part history:</Text>
                    <Badge size="lg" variant="light">{partNumber || '—'}</Badge>
                    <ClassBadge cls={partClass} size="sm" />
                    {selectedLine?.description && <Text size="xs" c="dimmed">{selectedLine.description}</Text>}
                  </Group>
                  {partNumber ? (
                    <>
                      <InventoryPanel partNumber={partNumber} />
                      <PricingPanel partNumber={partNumber} />
                      <SalesHistoryPanel partNumber={partNumber} acctno={acctno} partClass={partClass} />
                      <QuoteHistoryPanel partNumber={partNumber} acctno={acctno} partClass={partClass} />
                    </>
                  ) : <Text c="dimmed">Select a requested line to see its history.</Text>}
                </Stack>
              </Grid.Col>
            </Grid>
            </>
            )}
          </Stack>
        </Group>
      )}

      {/* The trace is drawn for ONE line — and beneath that, for ONE PHYSICAL UNIT. The pipeline resolves all
          the way down to the unit it would actually ship (the FIFO-oldest), and that unit's cost, cost basis,
          days-on-shelf and min sell price are what every number below is computed from. So the title names it:
          serial and DOS, not just the part. Without them a rep reads the cost and margin as facts about the
          part in the abstract, when they are facts about one item on one shelf. */}
      <MantineModal opened={explainOpen} onClose={() => setExplainOpen(false)} size="95%"
        title={(
          <Text fw={600} c={colorScheme === 'dark' ? 'gray.1' : 'dark.8'}>
            Decision trace — {deal.buyer_company_name || deal.customer_name_dw || cur?.deal_public_id}
            {partNumber ? ` · P/N: ${partNumber}` : ''}
            {selectedLine ? ` · Cond: ${selectedLine.condition_code || '—'}` : ''}
            {selectedLine ? ` · Class: ${selectedLine.part_class || '—'}` : ''}
            {lineScore?.ship_serial ? ` · S/N: ${lineScore.ship_serial}` : ''}
            {lineScore?.ship_dos_days != null ? ` · DOS: ${lineScore.ship_dos_days}` : ''}
            {lineScore?.min_sell_price ? ` · MIN $${Math.round(lineScore.min_sell_price).toLocaleString()}` : ''}
            {selectedLine?.description ? ` · Desc: ${selectedLine.description}` : ''}
          </Text>
        )}>
        {explainOpen && (
          <Tabs value={explainTab} onChange={setExplainTab}>
            {/* TWO GRAINS, TWO TRACES. The line trace may contain nothing computed across lines — prose about
                the deal's other parts on a screen showing THIS part is the contamination the pipeline exists
                to avoid. The deal trace is where the deal-grain steps (customer brief, deal summary) live.
                Before this split they had nowhere honest to go, so they were simply left out. */}
            <Tabs.List mb="sm">
              <Tabs.Tab value="line">This line</Tabs.Tab>
              <Tabs.Tab value="deal">The whole deal</Tabs.Tab>
            </Tabs.List>
            <Tabs.Panel value="line">
              <DecisionCanvas dealPublicId={cur?.deal_public_id} dealLineId={dealLineId} partNumber={partNumber}
                conditionCode={selectedLine?.condition_code} partClass={selectedLine?.part_class} />
            </Tabs.Panel>
            <Tabs.Panel value="deal">
              <DealCanvas
                dealPublicId={cur?.deal_public_id}
                onSelectLine={(id) => {
                  // Clicking a line branch is the way INTO its trace — select the line AND follow it there.
                  const idx = lines.findIndex((l) => l.deal_line_id === id);
                  if (idx >= 0) { setLineIdx(idx); setExplainTab('line'); }
                }}
              />
            </Tabs.Panel>
          </Tabs>
        )}
      </MantineModal>

      <TeachModal
        opened={!!teach}
        onClose={() => setTeach(null)}
        surface={teach?.surface}
        context={teach?.context || ''}
        idealOutput={teach?.idealOutput || ''}
        sourceRef={cur?.deal_public_id}
        onSaved={() => setTeach(null)}
      />
    </Stack>
  );
}

function Stat({ label, value }) {
  return (<div><Text size="xs" c="dimmed">{label}</Text><div>{typeof value === 'string' ? <Text size="sm">{value}</Text> : value}</div></div>);
}
